/**
 * The desktop sign-in exchange (docs/workbench-integration.md section 20): a desktop's device token
 * carries no sign-in the platform can date, so step-up (api `auth/stepUp.ts`) cannot judge a peer
 * token it asks for. The desktop presents the person's own sign-in once — on Enterprise the access
 * token its MSAL holds for the AIOE API audience, on Mojo Up AI Cloud the person's Cloud access
 * token — alongside its device token, and the platform keeps the dated sign-in on the device:
 *
 *   POST   /auth/v1/desktop/sign-in   { idToken } | { sessionToken }  -> DesktopSignInState
 *   GET    /auth/v1/desktop/sign-in                                   -> DesktopSignInState
 *   DELETE /auth/v1/desktop/sign-in                                   -> 204 (sign-out)
 *
 * Each with the device token (DPoP-bound when the device is). A gated action without a recent one
 * answers the step-up refusal every client already reads, `401 { error: "sign_in_again", message,
 * minutes }`, plus `how: "desktop-sign-in"` and `action`, so the desktop runs this exchange (after an
 * interactive sign-in when its own is too old) instead of starting a new enrolment. Step-up by action (0.72.0):
 * the platform refuses only taking a computer, within the organisation's step-up window. Attaching to a host is
 * minted on the device token, since a desktop attaches for background reads and reconnects too; the peer token
 * carries `stepUpUntil` (shared assignments.ts) and the node refuses changes once it has passed.
 */
import { z } from "zod";
export const DESKTOP_SIGN_IN_PATH = "/auth/v1/desktop/sign-in";
/** `how` on a step-up refusal the desktop answers by presenting its person's sign-in here. */
export const STEP_UP_HOW_DESKTOP_SIGN_IN = "desktop-sign-in";
/**
 * Actions that need a recent sign-in, by name. `node.roots.add` is a Control API method relayed for a
 * person; `computer.control` is a peer-token mint for a desktop (`computer` in `POST /mesh/v1/peer-token`).
 * `hosts.attach` was one before 0.72.0, and stays so an older platform's refusal still reads.
 */
export const STEP_UP_ACTIONS = ["node.roots.add", "hosts.attach", "computer.control"];
/** Exactly one of the two: `idToken` on Enterprise (the Entra or OIDC access token for the AIOE API), `sessionToken` on Cloud. */
export const desktopSignInRequestSchema = z.union([
    z.object({ idToken: z.string().min(1).max(16_384) }).strict(),
    z.object({ sessionToken: z.string().min(1).max(1_024) }).strict(),
]);
/**
 * Where `signedInAt` came from: `signed_in` is the token's `auth_time` (when the person last proved
 * who they are); `token_issued` is its `iat`, or a Cloud access token's issue, when there is none.
 */
export const desktopSignInFreshnessSchema = z.enum(["signed_in", "token_issued"]);
/** What the platform holds for this device. Times are ISO 8601. */
export const desktopSignInStateSchema = z.union([
    z.object({ signedIn: z.literal(false) }),
    z.object({
        signedIn: z.literal(true),
        /** The person's sign-in time, as the token dated it. */
        signedInAt: z.string(),
        freshness: desktopSignInFreshnessSchema,
        /** When the platform checked the token. */
        verifiedAt: z.string(),
        /** The record stops counting here (verifiedAt + AIOE_DESKTOP_SIGN_IN_HOURS, 12 by default). */
        validUntil: z.string(),
        /**
         * Until when it meets the organisation's step-up window: min(signedInAt + stepUpMinutes,
         * validUntil); validUntil when the organisation has step-up off. Past means: sign in
         * interactively, then present the new token.
         */
        stepUpUntil: z.string(),
        /** stepUpUntil is still ahead. */
        recent: z.boolean(),
        /**
         * Until when it lets the person change a node: min(signedInAt + AIOE_DESKTOP_SIGN_IN_HOURS, validUntil);
         * validUntil when step-up is off. The same moment rides in each peer token as `stepUpUntil` (0.72.0; on
         * 0.70.0 and 0.71.0 it was when attaching was refused). `stepUpUntil` here is what taking a computer
         * (`computer.control`) needs. Absent from a platform before 0.70.0.
         */
        attachUntil: z.string().optional(),
        /** The organisation's window in minutes (0: off). */
        stepUpMinutes: z.number().int().nonnegative(),
    }),
]);
//# sourceMappingURL=desktopSignIn.js.map